// Pallet Picks — a Pick Face module for orders that ship as whole pallets
// (special pick queue 5, BE migration 207). No wave and no tote: a pallet
// goes on a truck. The picker works ONE ORDER at a time, one pallet at a
// time:
//
//   0. start an order (tap Start, or just scan one of its pallets)
//                                                   → POST /pallet-picks/claim
//      The order is locked to this picker until every pallet of it is at
//      the holding area — the area is small, and an order's pallets must
//      arrive together rather than mixed in with another order's.
//   1. scan the pallet's label at its shelf        → POST /pallet-picks/pick
//   2. scan the pallet dispatch holding-area barcode → POST /pallet-picks/stage
//
// One scan input serves both scans: the holding barcode stages the pallet
// in hand, anything else is a pallet. When every pallet of the order is
// staged it leaves this list for the pallet dispatch desk and the picker is
// free to start the next.
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Forklift, Lock, MapPin, Undo2 } from "lucide-react";
import { toast } from "react-toastify";
import { usePageTitle } from "../hooks/usePageTitle";
import { useTranslation } from "../i18n";
import { apiFetch } from "../api/apiFetch";
import { playSuccess, playError } from "../components/helpers/audioPlayer";
import HiddenScanInput from "../components/HiddenScanInput";
import { useReadOnly } from "../access/ReadOnly";
import "../styles/picking.css";
import "../styles/pallet-picks.css";

export interface PalletPickLine {
    line_position: number;
    sku: string;
    title: string | null;
    size_name: string | null;
    palletways_type: string | null;
    need: number;
    picked: number;
    staged: number;
    outstanding: number;
    locations: string[];
    ready: boolean;
}
export interface PalletPickLock {
    name: string | null;
    mine: boolean;
    /** Idle for an hour with nothing in hand — another picker may take it over. */
    stale: boolean;
}
export interface PalletPickOrder {
    order_id: string;
    order_number: string | null;
    channel_created_at: string | null;
    status: string;
    locked_by: PalletPickLock | null;
    pallets: PalletPickLine[];
}
export interface PalletInHand {
    pick_id: number;
    order_id: string;
    order_number: string | null;
    sku: string;
    from_location: string | null;
}
interface PalletPicksData {
    enabled: boolean;
    orders: PalletPickOrder[];
    myOrderId: string | null;
    inHand: PalletInHand | null;
    holdingBarcode: string;
}

const REFRESH_MS = 15_000;

// BE refusal reasons that have their own message; anything else falls back
// to the generic one.
const REASON_KEYS = new Set([
    "stage_first", "unknown_barcode", "not_on_order", "no_stock",
    "not_holding_area", "nothing_in_hand",
    "order_locked", "not_in_your_order", "finish_order_first", "order_not_available", "order_cancelled",
]);

// Where a pallet line stands, for its chip.
export const palletLineState = (p: PalletPickLine): "to_pick" | "no_stock" | "moving" | "staged" => {
    if (p.outstanding > 0) return p.ready ? "to_pick" : "no_stock";
    if (p.picked > 0) return "moving";
    return "staged";
};

// Can this picker start the order? Not while they have another, and not
// while a different picker holds a live lock on it.
export const canStartOrder = (order: PalletPickOrder, myOrderId: string | null): boolean => {
    if (myOrderId !== null) return false;
    if (order.locked_by && !order.locked_by.mine && !order.locked_by.stale) return false;
    return order.pallets.some((p) => palletLineState(p) === "to_pick");
};

const PalletPicks = () => {
    usePageTitle("pallet_picks");
    const navigate = useNavigate();
    const { t } = useTranslation();
    const readOnly = useReadOnly();

    const [data, setData] = useState<PalletPicksData | null>(null);
    const [loadError, setLoadError] = useState(false);
    const [scanValue, setScanValue] = useState("");
    const [busy, setBusy] = useState(false);
    const [confirm, setConfirm] = useState<"release" | "leave" | null>(null);
    const mountedRef = useRef(true);

    const load = useCallback(async () => {
        try {
            const res = await apiFetch("/pallet-picks", {});
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const json = await res.json();
            if (!mountedRef.current) return;
            setData({
                enabled: json.enabled === true,
                orders: Array.isArray(json.orders) ? json.orders : [],
                myOrderId: json.myOrderId ?? null,
                inHand: json.inHand ?? null,
                holdingBarcode: String(json.holdingBarcode ?? ""),
            });
            setLoadError(false);
        } catch {
            if (mountedRef.current) setLoadError(true);
        }
    }, []);

    useEffect(() => {
        mountedRef.current = true;
        load();
        const timer = window.setInterval(load, REFRESH_MS);
        return () => {
            mountedRef.current = false;
            window.clearInterval(timer);
        };
    }, [load]);

    const fail = (reason: unknown, json: Record<string, unknown> = {}) => {
        playError();
        const key = typeof reason === "string" && REASON_KEYS.has(reason) ? `pallet_picks_err_${reason}` : "pallet_picks_err_generic";
        toast.error(t(key, {
            sku: String(json.sku ?? ""),
            name: String(json.picker_name ?? t("pallet_picks_another_picker")),
            order: String(json.order_number ?? json.order_id ?? ""),
        }), { toastId: `pallet-picks-${String(reason)}` });
    };

    const post = async (path: string, body: unknown) => {
        const res = await apiFetch(path, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
        });
        const json = await res.json().catch(() => ({}));
        return { ok: res.ok && json?.success === true, json };
    };

    // One request at a time; every outcome ends with a fresh list.
    const run = async (action: () => Promise<boolean>) => {
        if (busy) return;
        setBusy(true);
        try {
            if (await action()) await load();
        } catch {
            if (mountedRef.current) fail("generic");
        } finally {
            if (mountedRef.current) {
                setBusy(false);
                setConfirm(null);
            }
        }
    };

    const doSubmit = (scannedValue?: string) => {
        const val = (scannedValue ?? scanValue).trim();
        setScanValue("");
        if (!val || !data) return;
        run(async () => {
            const isHolding = data.holdingBarcode !== "" && val.toUpperCase() === data.holdingBarcode.toUpperCase();
            if (isHolding) {
                const { ok, json } = await post("/pallet-picks/stage", { barcode: val });
                if (!ok) { fail(json?.reason, json); return false; }
                playSuccess();
                const ready = Array.isArray(json.orderReady) && json.orderReady.length > 0;
                toast.success(t(ready ? "pallet_picks_staged_order_ready" : "pallet_picks_staged"), { toastId: "pallet-picks-staged" });
                return true;
            }
            const { ok, json } = await post("/pallet-picks/pick", { barcode: val });
            if (!ok) { fail(json?.reason, json); return false; }
            playSuccess();
            return true;
        });
    };

    const startOrder = (order: PalletPickOrder) => {
        if (readOnly) return;
        run(async () => {
            const { ok, json } = await post("/pallet-picks/claim", { order_id: order.order_id });
            if (!ok) { fail(json?.reason, json); await load(); return false; }
            return true;
        });
    };

    const releasePallet = () => {
        if (readOnly) return;
        run(async () => {
            const { ok, json } = await post("/pallet-picks/release", {});
            if (!ok) { fail(json?.reason, json); return false; }
            toast.info(t("pallet_picks_released"), { toastId: "pallet-picks-released" });
            return true;
        });
    };

    const leaveOrder = () => {
        if (readOnly) return;
        run(async () => {
            const { ok, json } = await post("/pallet-picks/unclaim", {});
            if (!ok) { fail(json?.reason, json); return false; }
            toast.info(t("pallet_picks_left_order"), { toastId: "pallet-picks-left" });
            return true;
        });
    };

    const inHand = data?.inHand ?? null;
    const orders = data?.orders ?? [];
    const myOrderId = data?.myOrderId ?? null;
    const myOrder = myOrderId ? orders.find((o) => o.order_id === myOrderId) ?? null : null;
    const otherOrders = orders.filter((o) => o.order_id !== myOrderId);
    // The next pallet of my order to fetch.
    const nextPallet = myOrder?.pallets.find((p) => palletLineState(p) === "to_pick") ?? null;

    const renderOrder = (order: PalletPickOrder, mine: boolean) => {
        const lock = order.locked_by;
        const lockedByOther = !!lock && !lock.mine && !lock.stale;
        return (
            <section
                key={order.order_id}
                className={`pallet-picks-order${mine ? " pallet-picks-order--mine" : ""}${!mine && (myOrderId !== null || lockedByOther) ? " pallet-picks-order--dim" : ""}`}
                aria-label={order.order_number ?? order.order_id}
            >
                <header className="pallet-picks-order-header">
                    <span className="pallet-picks-order-number">
                        {mine && <span className="pallet-picks-order-tag">{t("pallet_picks_your_order")}</span>}
                        {order.order_number ?? order.order_id}
                    </span>
                    <span className="pallet-picks-order-count">
                        {t("pallet_picks_order_progress", {
                            done: String(order.pallets.reduce((n, p) => n + p.staged, 0)),
                            total: String(order.pallets.reduce((n, p) => n + p.need, 0)),
                        })}
                    </span>
                    {!mine && lockedByOther && (
                        <span className="pallet-picks-order-lock">
                            <Lock size={14} aria-hidden="true" /> {t("pallet_picks_locked_by", { name: lock?.name ?? t("pallet_picks_another_picker") })}
                        </span>
                    )}
                    {!mine && canStartOrder(order, myOrderId) && (
                        <button type="button" className="btn btn-sm btn-primary" disabled={busy || readOnly} onClick={() => startOrder(order)}>
                            {t(lock?.stale ? "pallet_picks_take_over" : "pallet_picks_start")}
                        </button>
                    )}
                </header>
                {order.pallets.map((p) => {
                    const state = palletLineState(p);
                    return (
                        <div
                            key={`${order.order_id}-${p.line_position}`}
                            className={`pallet-picks-line pallet-picks-line--${state}${inHand?.sku === p.sku ? " pallet-picks-line--mine" : ""}`}
                        >
                            <div className="pallet-picks-line-main">
                                <span className="pallet-picks-sku">{p.sku}</span>
                                <span className="pallet-picks-size">
                                    {[p.size_name, p.palletways_type].filter(Boolean).join(" · ")}
                                </span>
                            </div>
                            <div className="pallet-picks-line-side">
                                {state === "to_pick" && (
                                    <span className="pallet-picks-location">
                                        <MapPin size={16} aria-hidden="true" /> {p.locations.join(", ")}
                                    </span>
                                )}
                                <span className={`pallet-picks-chip pallet-picks-chip--${state}`}>
                                    {t(`pallet_picks_state_${state}`)}
                                </span>
                            </div>
                        </div>
                    );
                })}
            </section>
        );
    };

    return (
        <div className="picking-selector pallet-picks">
            <div className="page-title-bar picking-selector-header">
                <h1 className="page-title">{t("pallet_picks")}</h1>
                <button
                    type="button"
                    className="page-back-btn"
                    onClick={() => navigate("/pick-face")}
                    aria-label={t("picking_queue_back")}
                >
                    <ArrowLeft size={20} />
                </button>
            </div>

            {inHand ? (
                <div className="pallet-picks-banner pallet-picks-banner--in-hand" role="status">
                    <Forklift size={28} aria-hidden="true" />
                    <div className="pallet-picks-banner-text">
                        <strong>{inHand.sku}</strong>
                        <span>{t("pallet_picks_take_to_holding", { barcode: data?.holdingBarcode ?? "" })}</span>
                    </div>
                    {confirm !== "release" ? (
                        <button type="button" className="btn btn-sm" disabled={busy || readOnly} onClick={() => setConfirm("release")}>
                            <Undo2 size={14} /> {t("pallet_picks_put_back")}
                        </button>
                    ) : (
                        <span className="pallet-picks-confirm">
                            <button type="button" className="btn btn-sm btn-danger" disabled={busy || readOnly} onClick={releasePallet}>
                                {t("pallet_picks_put_back_confirm")}
                            </button>
                            <button type="button" className="btn btn-sm" disabled={busy} onClick={() => setConfirm(null)}>
                                {t("cancel")}
                            </button>
                        </span>
                    )}
                </div>
            ) : myOrder ? (
                <div className="pallet-picks-banner pallet-picks-banner--next" role="status">
                    <Forklift size={28} aria-hidden="true" />
                    <div className="pallet-picks-banner-text">
                        {nextPallet ? (
                            <>
                                <strong>{t("pallet_picks_next", { sku: nextPallet.sku })}</strong>
                                <span>{t("pallet_picks_next_at", { location: nextPallet.locations.join(", ") })}</span>
                            </>
                        ) : (
                            <>
                                <strong>{t("pallet_picks_nothing_pickable")}</strong>
                                <span>{t("pallet_picks_nothing_pickable_hint")}</span>
                            </>
                        )}
                    </div>
                    {confirm !== "leave" ? (
                        <button type="button" className="btn btn-sm" disabled={busy || readOnly} onClick={() => setConfirm("leave")}>
                            {t("pallet_picks_leave_order")}
                        </button>
                    ) : (
                        <span className="pallet-picks-confirm">
                            <button type="button" className="btn btn-sm btn-danger" disabled={busy || readOnly} onClick={leaveOrder}>
                                {t("pallet_picks_leave_order_confirm")}
                            </button>
                            <button type="button" className="btn btn-sm" disabled={busy} onClick={() => setConfirm(null)}>
                                {t("cancel")}
                            </button>
                        </span>
                    )}
                </div>
            ) : (
                <div className="pallet-picks-banner" role="status">
                    <Forklift size={28} aria-hidden="true" />
                    <div className="pallet-picks-banner-text">
                        <strong>{t("pallet_picks_start_an_order")}</strong>
                        <span>{t("pallet_picks_start_an_order_hint")}</span>
                    </div>
                </div>
            )}

            {data && !data.enabled && (
                <p className="pallet-picks-note">{t("pallet_picks_not_enabled")}</p>
            )}
            {loadError && <p className="pallet-picks-note pallet-picks-note--error">{t("server_error")}</p>}

            {data && orders.length === 0 && !loadError && (
                <p className="pallet-picks-empty">{t("pallet_picks_empty")}</p>
            )}

            <div className="pallet-picks-list">
                {myOrder && renderOrder(myOrder, true)}
                {myOrder && otherOrders.length > 0 && (
                    <p className="pallet-picks-note pallet-picks-note--inline">{t("pallet_picks_finish_first", { n: String(otherOrders.length) })}</p>
                )}
                {otherOrders.map((order) => renderOrder(order, false))}
            </div>

            <HiddenScanInput
                value={scanValue}
                onChange={setScanValue}
                onSubmit={doSubmit}
                disabled={busy || !data}
                disableCamera
            />
        </div>
    );
};

export default PalletPicks;
